import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { HelpIcon } from '@/pages/fulfillment/components/UltimateIcons';
import './helpTip.css';

export interface HelpTipProps {
  /** What the help explains; the button reads "What is <topic>?" and the panel opens under that title. */
  topic: string;
  /** One short paragraph each, in the order the desk needs them; no two are the same sentence. */
  lines: string[];
}

/** The panel's width at most, and the gap it keeps from the edge of whatever it opens inside. */
const PANEL_WIDTH = 420;
const MIN_PANEL_WIDTH = 240;
const EDGE_GAP = 16;

/**
 * A (?) beside a section title that opens what the section is for and how to use it [SSD-7472]. It is a disclosure,
 * not a hover tooltip: a click opens it and it stays while a paragraph is read, until a second click, Escape or a
 * click elsewhere. Nothing is rendered while it is closed, so the title and any alert beside it read as before.
 */
export const HelpTip = ({ topic, lines }: HelpTipProps) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLSpanElement>(null);
  const panelId = useId();
  /** Where the panel sits under the button, worked out when it opens so it never runs past an edge. */
  const [place, setPlace] = useState<{ left: number; width: number } | null>(null);

  // When it opens, before paint, the panel is moved inside the dialog it opens in, or the window: a (?) at the end of
  // a wrapped line would otherwise push it off the left or right edge [SSD-7472 QA]. A box narrower than the
  // smallest readable panel keeps the panel's left edge in view and lets the right run over.
  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    if (!open || !wrap) {
      setPlace(null);
      return;
    }
    const at = wrap.getBoundingClientRect();
    const box = wrap.closest('dialog')?.getBoundingClientRect() ?? { left: 0, right: window.innerWidth };
    const width = Math.min(PANEL_WIDTH, Math.max(MIN_PANEL_WIDTH, box.right - box.left - EDGE_GAP * 2));
    const lowest = box.left + EDGE_GAP - at.left;
    const highest = box.right - EDGE_GAP - width - at.left;
    setPlace({ left: highest < lowest ? lowest : Math.min(Math.max(-8, lowest), highest), width });
  }, [open]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onPointer = (event: MouseEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    // Captured, so Escape folds the help before a dialog around it takes the key as "close the dialog".
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [open]);

  return (
    <span className="ff-help" ref={wrapRef}>
      <button
        type="button"
        className="ff-help-btn"
        aria-label={`What is ${topic}?`}
        title={`What is ${topic}?`}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={() => setOpen((shown) => !shown)}
      >
        <HelpIcon />
      </button>
      {open && (
        <span
          id={panelId}
          role="note"
          aria-label={topic}
          className="ff-help-panel"
          style={place ? { left: place.left, width: place.width } : undefined}
        >
          <span className="ff-help-title">{topic}</span>
          {lines.map((line) => (
            <span key={line} className="ff-help-p">
              {line}
            </span>
          ))}
        </span>
      )}
    </span>
  );
};
